import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { DateField, TimeField } from '@/ui/DateTime';
import { Icon } from '@/ui/Icon';
import type { IconName } from '@/ui/icons';
import { MenuHead, MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import { parseLocalDateTime, toDateInput, toTimeInput } from '@/lib/dates';
import { formatDay, formatDayTime } from '@/lib/format';
import { zoneCity } from '../util';
import { MOD } from './Editor';
import { scheduleProblem, schedulePresets } from './logic';

// The Send button with its schedule menu: send now, a quick time, or a date and time you pick. PickTime is the one
// date and time picker of the mail area: scheduling a send, starting a list send, snoozing and rescheduling.

function sameMinute(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && Math.abs(a.getTime() - b.getTime()) < 60_000;
}

export function SendSplit({
  sendAt,
  onSchedule,
  onSend,
  busy,
  disabled,
}: {
  sendAt: Date | null;
  onSchedule: (at: Date | null) => void;
  onSend: () => void;
  busy: boolean;
  disabled?: boolean;
}) {
  const pop = usePopover('menu');
  const [picking, setPicking] = useState(false);
  // The quick times follow the clock: they are worked out again each time the menu opens.
  const now = useMemo(() => new Date(), [pop.open]);
  const presets = useMemo(() => schedulePresets(now), [now]);
  const custom = sendAt && !presets.some((p) => sameMinute(p.at, sendAt)) ? sendAt : null;

  useEffect(() => {
    if (!pop.open) setPicking(false);
  }, [pop.open]);

  return (
    <div className="split">
      <button type="button" className="btn btn-primary" onClick={onSend} aria-disabled={busy || disabled || undefined} aria-busy={busy || undefined}>
        <Icon name={sendAt ? 'clock' : 'send'} />
        <span>{busy ? (sendAt ? 'Scheduling' : 'Sending') : sendAt ? 'Schedule' : 'Send'}</span>
        <kbd className="kbd">{MOD === '⌘' ? '⌘↵' : 'Ctrl ↵'}</kbd>
      </button>
      <button type="button" className="btn btn-primary" aria-label="Schedule options" data-tip="Schedule" {...pop.anchorProps}>
        <Icon name="chev-up" />
      </button>
      <Popover state={pop} width={340} maxWidth={340} className="sched-menu" role={picking ? 'dialog' : 'menu'} label={picking ? 'Pick a time' : 'Schedule options'}>
        {picking ? (
          <PickTime
            initial={sendAt ?? presets.find((p) => p.id === 'tomorrow')?.at ?? new Date(now.getTime() + 3_600_000)}
            onBack={() => setPicking(false)}
            onPick={(at) => {
              onSchedule(at);
              pop.close();
            }}
          />
        ) : (
          <>
            <MenuHead>Send</MenuHead>
            <MenuItem icon="send" checked={!sendAt} onSelect={() => onSchedule(null)}>
              Send now
            </MenuItem>
            {presets.map((p) => (
              <MenuItem key={p.id} icon={p.icon} sub={p.sub} checked={sameMinute(p.at, sendAt)} onSelect={() => onSchedule(p.at)}>
                {p.label}
              </MenuItem>
            ))}
            {custom ? (
              <MenuItem icon="calendar" sub="Picked" checked onSelect={() => onSchedule(custom)}>
                {formatDayTime(custom)}
              </MenuItem>
            ) : null}
            <MenuSep />
            <MenuItem icon="calendar" keep sub={<Icon name="chev-right" />} onSelect={() => setPicking(true)}>
              Pick a time
            </MenuItem>
          </>
        )}
      </Popover>
    </div>
  );
}

export function PickTime({
  initial,
  onPick,
  onBack,
  action = 'Schedule',
  icon = 'clock',
}: {
  initial: Date;
  onPick: (at: Date) => void;
  onBack?: () => void;
  action?: string;
  icon?: IconName;
}) {
  const [date, setDate] = useState(toDateInput(initial));
  const [time, setTime] = useState(toTimeInput(initial));
  const [error, setError] = useState<string | null>(null);
  const noteId = useId();
  const now = new Date();
  const at = parseLocalDateTime(date, time);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = scheduleProblem(at, new Date());
    if (problem) {
      setError(problem);
      return;
    }
    onPick(at!);
  };

  return (
    <form className="sched-pick" onSubmit={submit} noValidate>
      <div className="mhead">Pick a time</div>
      <div className="sched-pick-row">
        <DateField
          label="Date"
          value={date}
          autoFocus
          min={toDateInput(now)}
          max={toDateInput(new Date(now.getTime() + 364 * 86_400_000))}
          onChange={(next) => {
            setDate(next);
            setError(null);
          }}
          aria-invalid={!!error}
          aria-describedby={noteId}
          required
        />
        <TimeField
          label="Time"
          value={time}
          step={5}
          onChange={(next) => {
            setTime(next);
            setError(null);
          }}
          aria-invalid={!!error}
          aria-describedby={noteId}
          required
        />
      </div>
      {error ? (
        <p className="field-err" id={noteId} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : (
        <p className="field-help" id={noteId}>
          {at ? `${formatDay(at)}, your time (${zoneCity()})` : `Your time (${zoneCity()})`}
        </p>
      )}
      <div className="sched-pick-f">
        {onBack ? (
          <button type="button" className="btn btn-sm btn-ghost" onClick={onBack}>
            <Icon name="chev-left" />
            Back
          </button>
        ) : null}
        <span className="spacer" />
        <button type="submit" className="btn btn-sm btn-primary">
          <Icon name={icon} />
          {action}
        </button>
      </div>
    </form>
  );
}
